import type { ReactNode } from 'react';
import { PLACES } from './places';

export type ScenePart = {
  id: string;
  label: string;
  at: [number, number];
  box: [number, number, number, number];
  art?: ReactNode;
  wear?: string;
  wearSize?: number;
  worn?: ReactNode;
  mini?: ReactNode;
  hit?: [number, number, number, number];
  behind?: boolean;
  inside?: boolean;
};

export type Scene = {
  id: string;
  label: string;
  kind: 'parts' | 'dress' | 'room' | 'place';
  w: number;
  h: number;
  base: ReactNode;
  mid?: ReactNode;
  front?: ReactNode;
  parts: ScenePart[];
};

const INK = '#3a3a3a';

const house: Scene = {
  id: 'house',
  label: 'House',
  kind: 'parts',
  w: 400,
  h: 300,
  base: (
    <>
      <rect width="400" height="300" fill="#cfe8f7" />
      <circle cx="350" cy="48" r="26" fill="#ffd75e" />
      <ellipse cx="90" cy="52" rx="38" ry="14" fill="#fff" opacity="0.9" />
      <rect y="230" width="400" height="70" fill="#8cc56a" />
      <circle cx="78" cy="232" r="20" fill="#5e9c45" />
      <circle cx="332" cy="234" r="16" fill="#5e9c45" />
    </>
  ),
  parts: [
    { id: 'wall', label: 'wall', at: [140, 215], box: [105, 125, 190, 120], art: <rect x="110" y="130" width="180" height="110" fill="#f3d9b1" stroke="#b9925c" strokeWidth="3" /> },
    { id: 'roof', label: 'roof', at: [200, 108], box: [88, 55, 224, 88], art: <polygon points="95,135 200,60 305,135" fill="#d9583b" stroke="#a63e27" strokeWidth="3" strokeLinejoin="round" /> },
    {
      id: 'chimney', label: 'chimney', at: [258, 92], box: [232, 40, 52, 82], art: (
        <g>
          <circle cx="262" cy="52" r="7" fill="#e6eef3" />
          <circle cx="272" cy="44" r="9" fill="#e6eef3" />
          <rect x="245" y="72" width="26" height="46" fill="#b5523a" stroke="#7d3423" strokeWidth="2" />
          <rect x="241" y="66" width="34" height="10" fill="#8f3f2b" />
        </g>
      ),
    },
    {
      id: 'door', label: 'door', at: [200, 208], box: [176, 172, 48, 74], art: (
        <g>
          <rect x="182" y="178" width="36" height="62" rx="4" fill="#7a4a2a" stroke="#4f2f19" strokeWidth="2" />
          <circle cx="211" cy="212" r="3.5" fill="#ffd75e" />
        </g>
      ),
    },
    {
      id: 'window', label: 'window', at: [148, 170], box: [122, 146, 52, 48], art: (
        <g fill="#bfe3f7" stroke="#fff" strokeWidth="3">
          <rect x="128" y="152" width="40" height="36" />
          <line x1="148" y1="152" x2="148" y2="188" />
          <line x1="128" y1="170" x2="168" y2="170" />
          <rect x="232" y="152" width="40" height="36" />
          <line x1="252" y1="152" x2="252" y2="188" />
          <line x1="232" y1="170" x2="272" y2="170" />
        </g>
      ),
    },
    { id: 'path', label: 'garden path', at: [200, 272], box: [150, 236, 100, 64], art: <polygon points="182,240 218,240 248,300 152,300" fill="#d8c7a3" stroke="#b8a37a" strokeWidth="2" /> },
    {
      id: 'fence', label: 'fence', at: [42, 226], box: [0, 200, 104, 50], art: (
        <g fill="#fff" stroke="#c9c2b2" strokeWidth="1.5">
          <rect x="0" y="219" width="100" height="6" />
          <rect x="0" y="234" width="100" height="6" />
          {[4, 24, 44, 64, 84].map((x) => <path key={x} d={`M${x} 246 V212 L${x + 6} 205 L${x + 12} 212 V246 Z`} />)}
          <rect x="300" y="219" width="100" height="6" />
          <rect x="300" y="234" width="100" height="6" />
          {[304, 324, 344, 364, 384].map((x) => <path key={x} d={`M${x} 246 V212 L${x + 6} 205 L${x + 12} 212 V246 Z`} />)}
        </g>
      ),
    },
  ],
};

const plane: Scene = {
  id: 'plane',
  label: 'Aeroplane',
  kind: 'parts',
  w: 400,
  h: 300,
  base: (
    <>
      <rect width="400" height="300" fill="#d6ecfb" />
      <ellipse cx="80" cy="250" rx="50" ry="16" fill="#fff" />
      <ellipse cx="330" cy="60" rx="44" ry="14" fill="#fff" />
    </>
  ),
  parts: [
    {
      id: 'tail', label: 'tail', at: [72, 96], box: [24, 62, 88, 98], art: (
        <g fill="#d9583b" stroke="#a63e27" strokeWidth="2" strokeLinejoin="round">
          <polygon points="76,126 52,70 84,70 108,126" />
          <polygon points="66,142 30,150 30,156 70,156" />
        </g>
      ),
    },
    { id: 'body', label: 'body', at: [250, 150], box: [62, 116, 290, 56], art: <path d="M70 128 Q70 122 90 122 L310 122 Q350 124 352 146 Q350 166 312 168 L90 168 Q70 168 70 158 Z" fill="#f4f6f9" stroke="#9fb0c2" strokeWidth="3" /> },
    { id: 'cockpit', label: 'cockpit', at: [318, 136], box: [294, 122, 50, 26], art: <path d="M302 130 L326 128 Q340 132 342 142 L302 142 Z" fill="#3b6ea8" stroke="#2b507a" strokeWidth="2" /> },
    { id: 'windows', label: 'windows', at: [200, 140], box: [116, 130, 176, 20], art: <g fill="#8fbbe8" stroke="#5f8fbf" strokeWidth="1.5">{[130, 152, 174, 196, 218, 240, 262, 284].map((x) => <circle key={x} cx={x} cy="140" r="5.5" />)}</g> },
    { id: 'door', label: 'door', at: [108, 145], box: [94, 124, 28, 40], art: <rect x="100" y="128" width="16" height="30" rx="4" fill="#dfe6ee" stroke="#8a9bb0" strokeWidth="2" /> },
    { id: 'wing', label: 'wing', at: [168, 196], box: [140, 146, 104, 82], art: <polygon points="158,152 232,152 196,224 150,224" fill="#c9d6e3" stroke="#8a9bb0" strokeWidth="2.5" strokeLinejoin="round" /> },
    { id: 'engine', label: 'engine', at: [214, 206], box: [194, 192, 54, 28], art: <g><rect x="198" y="196" width="46" height="20" rx="10" fill="#7f93a8" stroke="#55677a" strokeWidth="2" /><circle cx="242" cy="206" r="6" fill="#55677a" /></g> },
    {
      id: 'wheels', label: 'wheels', at: [290, 186], box: [104, 162, 204, 34], art: (
        <g stroke={INK} strokeWidth="3">
          <line x1="120" y1="168" x2="120" y2="180" />
          <line x1="290" y1="168" x2="290" y2="180" />
          <circle cx="120" cy="184" r="8" fill="#333" />
          <circle cx="290" cy="184" r="8" fill="#333" />
        </g>
      ),
    },
  ],
};

const face: Scene = {
  id: 'face',
  label: 'Face',
  kind: 'parts',
  w: 400,
  h: 300,
  base: <rect width="400" height="300" fill="#fdf1e4" />,
  parts: [
    { id: 'ears', label: 'ears', at: [100, 162], box: [80, 138, 40, 48], art: <g fill="#efc095" stroke="#c99470" strokeWidth="2"><circle cx="105" cy="162" r="18" /><circle cx="295" cy="162" r="18" /></g> },
    { id: 'chin', label: 'chin', at: [200, 244], box: [150, 200, 100, 56], art: <circle cx="200" cy="155" r="95" fill="#f5cfa6" stroke="#c99470" strokeWidth="2" /> },
    { id: 'hair', label: 'hair', at: [200, 74], box: [104, 44, 196, 96], art: <path d="M108 140 Q112 52 200 50 Q288 52 294 140 Q270 96 200 92 Q132 96 108 140 Z" fill="#5b3a1e" /> },
    { id: 'eyebrows', label: 'eyebrows', at: [165, 117], box: [144, 104, 112, 26], art: <g fill="none" stroke="#5b3a1e" strokeWidth="5" strokeLinecap="round"><path d="M150 122 Q165 111 180 121" /><path d="M220 121 Q235 111 250 122" /></g> },
    {
      id: 'eyes', label: 'eyes', at: [165, 141], box: [146, 122, 108, 38], art: (
        <g>
          <ellipse cx="165" cy="141" rx="13" ry="15" fill="#fff" stroke="#c99470" strokeWidth="1.5" />
          <ellipse cx="235" cy="141" rx="13" ry="15" fill="#fff" stroke="#c99470" strokeWidth="1.5" />
          <circle cx="167" cy="143" r="6.5" fill="#3b2a1a" />
          <circle cx="237" cy="143" r="6.5" fill="#3b2a1a" />
        </g>
      ),
    },
    { id: 'nose', label: 'nose', at: [200, 170], box: [184, 146, 32, 42], art: <path d="M200 150 Q190 176 197 182 Q205 186 212 179" fill="#e8b48a" stroke="#c99470" strokeWidth="2" /> },
    { id: 'cheeks', label: 'cheeks', at: [256, 186], box: [126, 168, 148, 34], art: <g fill="#f5a5a0" opacity="0.7"><circle cx="146" cy="186" r="14" /><circle cx="254" cy="186" r="14" /></g> },
    { id: 'mouth', label: 'mouth', at: [200, 212], box: [164, 198, 72, 32], art: <path d="M170 206 Q200 232 230 206 Q200 218 170 206 Z" fill="#c94a4a" stroke="#9d3434" strokeWidth="2" /> },
  ],
};

const SKIN = '#f5cfa6';

function figure(withClothes: boolean) {
  return (
    <g>
      <rect x="190" y="78" width="20" height="14" fill={SKIN} />
      <g stroke={SKIN} strokeWidth="17" strokeLinecap="round" fill="none">
        <path d="M168 100 L150 133 L136 166" />
        <path d="M232 100 L250 133 L264 166" />
        <path d="M186 200 L184 245 L182 288" />
        <path d="M214 200 L216 245 L218 288" />
      </g>
      <circle cx="134" cy="172" r="11" fill={SKIN} />
      <circle cx="266" cy="172" r="11" fill={SKIN} />
      <rect x="164" y="90" width="72" height="84" rx="16" fill={withClothes ? '#5aa0d8' : '#f2f2f2'} stroke={withClothes ? '#3d7fb6' : '#cfcfcf'} strokeWidth="2" />
      <rect x="168" y="168" width="64" height="36" rx="8" fill={withClothes ? '#3b5c8a' : '#b8c6d6'} />
      <ellipse cx="178" cy="296" rx="16" ry="8" fill={withClothes ? '#6b4a2b' : SKIN} />
      <ellipse cx="222" cy="296" rx="16" ry="8" fill={withClothes ? '#6b4a2b' : SKIN} />
      <circle cx="200" cy="52" r="30" fill={SKIN} stroke="#c99470" strokeWidth="2" />
      <path d="M170 48 Q172 18 200 18 Q228 18 230 48 Q214 34 200 34 Q186 34 170 48 Z" fill="#5b3a1e" />
      <circle cx="190" cy="52" r="3.5" fill="#3b2a1a" />
      <circle cx="210" cy="52" r="3.5" fill="#3b2a1a" />
      <path d="M190 64 Q200 71 210 64" fill="none" stroke="#9d3434" strokeWidth="2.5" strokeLinecap="round" />
    </g>
  );
}

const body: Scene = {
  id: 'body',
  label: 'Body',
  kind: 'parts',
  w: 400,
  h: 320,
  base: <><rect width="400" height="320" fill="#eef5fb" />{figure(true)}</>,
  parts: [
    { id: 'head', label: 'head', at: [244, 40], box: [166, 16, 68, 70], hit: [168, 18, 64, 66], art: <circle cx="200" cy="52" r="30" fill={SKIN} stroke="#c99470" strokeWidth="2" /> },
    { id: 'shoulder', label: 'shoulder', at: [166, 98], box: [148, 84, 40, 32], hit: [150, 86, 36, 28] },
    { id: 'arm', label: 'arm', at: [252, 132], box: [228, 92, 50, 84], hit: [236, 96, 30, 60], art: <path d="M232 100 L250 133 L264 166" stroke={SKIN} strokeWidth="17" strokeLinecap="round" fill="none" /> },
    { id: 'elbow', label: 'elbow', at: [148, 133], box: [134, 118, 30, 30], hit: [134, 118, 30, 30] },
    { id: 'hand', label: 'hand', at: [134, 174], box: [118, 158, 32, 32], hit: [118, 158, 32, 32], art: <circle cx="134" cy="172" r="11" fill={SKIN} stroke="#c99470" strokeWidth="1.5" /> },
    { id: 'tummy', label: 'tummy', at: [200, 140], box: [164, 100, 72, 76], hit: [170, 112, 60, 56] },
    { id: 'knee', label: 'knee', at: [184, 246], box: [170, 232, 28, 28], hit: [170, 232, 28, 28] },
    { id: 'leg', label: 'leg', at: [216, 268], box: [200, 200, 34, 96], hit: [205, 255, 24, 30], art: <path d="M214 200 L216 245 L218 288" stroke={SKIN} strokeWidth="17" strokeLinecap="round" fill="none" /> },
    { id: 'foot', label: 'foot', at: [178, 298], box: [158, 286, 40, 22], hit: [160, 286, 38, 20], art: <ellipse cx="178" cy="296" rx="16" ry="8" fill="#6b4a2b" /> },
  ],
};

const car: Scene = {
  id: 'car',
  label: 'Car',
  kind: 'parts',
  w: 400,
  h: 300,
  base: (
    <>
      <rect width="400" height="300" fill="#e3f0f8" />
      <rect y="226" width="400" height="74" fill="#9aa3ad" />
      <rect y="258" width="400" height="6" fill="#fff" opacity="0.6" />
    </>
  ),
  parts: [
    { id: 'body', label: 'body', at: [130, 170], box: [36, 100, 340, 104], art: <path d="M40 200 L40 166 Q45 150 80 148 L130 112 Q140 104 160 104 L250 104 Q268 104 280 116 L310 148 L355 152 Q372 156 372 175 L372 200 Z" fill="#e2574c" stroke="#a83a31" strokeWidth="3" strokeLinejoin="round" /> },
    { id: 'boot', label: 'boot', at: [62, 160], box: [38, 144, 62, 30], art: <path d="M44 168 Q46 153 80 150 L96 150 L96 168 Z" fill="#ea6c62" stroke="#a83a31" strokeWidth="1.5" /> },
    { id: 'bonnet', label: 'bonnet', at: [336, 160], box: [300, 146, 74, 24], art: <path d="M302 151 L355 154 Q368 157 370 166 L306 166 Z" fill="#ea6c62" stroke="#a83a31" strokeWidth="1.5" /> },
    { id: 'window', label: 'window', at: [190, 130], box: [136, 108, 170, 44], art: <g fill="#bfe3f7" stroke="#8fb9d6" strokeWidth="2"><polygon points="142,148 166,113 222,113 222,148" /><polygon points="232,113 260,113 298,148 232,148" /></g> },
    { id: 'steering', label: 'steering wheel', at: [262, 136], box: [248, 124, 28, 26], art: <circle cx="262" cy="137" r="8" fill="none" stroke={INK} strokeWidth="3" /> },
    { id: 'door', label: 'door', at: [190, 176], box: [146, 146, 104, 54], art: <g><path d="M150 152 L250 152 L250 196 L156 196 Q150 190 150 182 Z" fill="none" stroke="#a83a31" strokeWidth="2.5" /><rect x="230" y="160" width="14" height="4" rx="2" fill="#a83a31" /></g> },
    { id: 'headlight', label: 'headlight', at: [362, 166], box: [350, 156, 24, 20], art: <ellipse cx="363" cy="166" rx="8" ry="6" fill="#fff3b0" stroke="#d6b84a" strokeWidth="1.5" /> },
    { id: 'plate', label: 'number plate', at: [56, 182], box: [38, 172, 36, 20], art: <g><rect x="42" y="176" width="28" height="12" rx="2" fill="#ffd75e" stroke={INK} strokeWidth="1.5" /><text x="56" y="185.5" fontSize="8" fontFamily="sans-serif" textAnchor="middle" fill={INK}>AB 12</text></g> },
    { id: 'bumper', label: 'bumper', at: [372, 198], box: [358, 190, 24, 16], art: <g fill="#9aa3ad" stroke="#6c757e" strokeWidth="1.5"><rect x="362" y="192" width="16" height="10" rx="3" /><rect x="30" y="192" width="16" height="10" rx="3" /></g> },
    {
      id: 'wheels', label: 'wheels', at: [105, 202], box: [76, 172, 58, 58], art: (
        <g>
          <circle cx="105" cy="200" r="26" fill="#2b2b2b" />
          <circle cx="105" cy="200" r="10" fill="#c3c9cf" />
          <circle cx="300" cy="200" r="26" fill="#2b2b2b" />
          <circle cx="300" cy="200" r="10" fill="#c3c9cf" />
        </g>
      ),
    },
  ],
};

const bicycle: Scene = {
  id: 'bicycle',
  label: 'Bicycle',
  kind: 'parts',
  w: 400,
  h: 300,
  base: <><rect width="400" height="300" fill="#eef7ec" /><rect y="255" width="400" height="45" fill="#cfe3c4" /></>,
  parts: [
    {
      id: 'wheels', label: 'wheels', at: [60, 200], box: [50, 140, 120, 120], art: (
        <g fill="none" stroke="#333" strokeWidth="7">
          <circle cx="110" cy="200" r="55" />
          <circle cx="290" cy="200" r="55" />
          <g strokeWidth="1.5" stroke="#999">
            {[0, 30, 60, 90, 120, 150].map((a) => <line key={a} x1={110 + 52 * Math.cos((a * Math.PI) / 180)} y1={200 + 52 * Math.sin((a * Math.PI) / 180)} x2={110 - 52 * Math.cos((a * Math.PI) / 180)} y2={200 - 52 * Math.sin((a * Math.PI) / 180)} />)}
            {[0, 30, 60, 90, 120, 150].map((a) => <line key={a} x1={290 + 52 * Math.cos((a * Math.PI) / 180)} y1={200 + 52 * Math.sin((a * Math.PI) / 180)} x2={290 - 52 * Math.cos((a * Math.PI) / 180)} y2={200 - 52 * Math.sin((a * Math.PI) / 180)} />)}
          </g>
        </g>
      ),
    },
    { id: 'chain', label: 'chain', at: [150, 212], box: [104, 186, 100, 30], art: <path d="M110 194 L195 192 Q205 200 195 208 L110 206 Z" fill="none" stroke="#777" strokeWidth="3" strokeDasharray="3 2" /> },
    { id: 'frame', label: 'frame', at: [232, 160], box: [104, 110, 194, 96], art: <g fill="none" stroke="#2f7fb8" strokeWidth="7" strokeLinejoin="round" strokeLinecap="round"><polyline points="110,200 180,122 195,200 110,200" /><polyline points="180,122 270,120 195,200" /><line x1="270" y1="120" x2="290" y2="200" /></g> },
    { id: 'seat', label: 'seat', at: [168, 104], box: [150, 94, 44, 30], art: <g><line x1="180" y1="122" x2="176" y2="110" stroke="#333" strokeWidth="5" /><path d="M156 106 Q170 98 190 104 Q186 112 170 112 Q158 112 156 106 Z" fill="#333" /></g> },
    { id: 'handlebars', label: 'handlebars', at: [246, 90], box: [240, 78, 54, 46], art: <g fill="none" stroke="#333" strokeWidth="5" strokeLinecap="round"><line x1="270" y1="120" x2="264" y2="92" /><path d="M248 92 Q264 86 288 90" /></g> },
    { id: 'bell', label: 'bell', at: [288, 104], box: [276, 92, 24, 24], art: <circle cx="286" cy="99" r="6" fill="#ffd75e" stroke="#c79a2a" strokeWidth="1.5" /> },
    { id: 'pedal', label: 'pedal', at: [212, 226], box: [188, 194, 40, 38], art: <g><line x1="195" y1="200" x2="207" y2="222" stroke="#333" strokeWidth="5" strokeLinecap="round" /><rect x="198" y="220" width="20" height="7" rx="2" fill="#555" /></g> },
  ],
};

const flower: Scene = {
  id: 'flower',
  label: 'Flower',
  kind: 'parts',
  w: 400,
  h: 300,
  base: <><rect width="400" height="210" fill="#e4f3fb" /><rect y="210" width="400" height="90" fill="#8a5a33" /><rect y="210" width="400" height="8" fill="#5e9c45" /></>,
  parts: [
    { id: 'roots', label: 'roots', at: [200, 262], box: [160, 208, 80, 80], art: <g fill="none" stroke="#e2c49b" strokeWidth="3" strokeLinecap="round"><path d="M200 212 Q196 240 182 262" /><path d="M200 212 Q205 246 220 270" /><path d="M200 214 Q200 250 198 284" /><path d="M190 240 Q178 246 170 244" /></g> },
    { id: 'seed', label: 'seed', at: [290, 254], box: [276, 242, 30, 24], art: <ellipse cx="290" cy="254" rx="9" ry="6" fill="#d8b06b" stroke="#a07a3c" strokeWidth="1.5" /> },
    { id: 'stem', label: 'stem', at: [200, 190], box: [184, 90, 32, 124], art: <path d="M200 212 Q194 152 200 96" fill="none" stroke="#3f8f34" strokeWidth="8" strokeLinecap="round" /> },
    { id: 'leaf', label: 'leaf', at: [170, 168], box: [146, 132, 108, 60], art: <g fill="#5aaa45" stroke="#3f8f34" strokeWidth="2"><path d="M198 172 Q170 140 150 160 Q168 184 198 172 Z" /><path d="M202 150 Q232 124 250 142 Q232 166 202 150 Z" /></g> },
    { id: 'petal', label: 'petals', at: [234, 60], box: [150, 30, 100, 100], art: <g fill="#f472a5" stroke="#d24f84" strokeWidth="1.5">{[0, 45, 90, 135, 180, 225, 270, 315].map((a) => <ellipse key={a} cx={200 + 28 * Math.cos((a * Math.PI) / 180)} cy={80 + 28 * Math.sin((a * Math.PI) / 180)} rx="17" ry="11" transform={`rotate(${a} ${200 + 28 * Math.cos((a * Math.PI) / 180)} ${80 + 28 * Math.sin((a * Math.PI) / 180)})`} />)}</g> },
    { id: 'middle', label: 'middle', at: [200, 80], box: [180, 60, 40, 40], art: <circle cx="200" cy="80" r="16" fill="#f2b425" stroke="#c78a12" strokeWidth="2" /> },
  ],
};

const fish: Scene = {
  id: 'fish',
  label: 'Fish',
  kind: 'parts',
  w: 400,
  h: 300,
  base: <><rect width="400" height="300" fill="#bfe6f5" />{[60, 340, 120].map((x, i) => <circle key={x} cx={x} cy={40 + i * 90} r={6 + i * 2} fill="none" stroke="#fff" strokeWidth="2" />)}<rect y="270" width="400" height="30" fill="#e8d6a6" /></>,
  parts: [
    { id: 'tail', label: 'tail', at: [58, 150], box: [34, 94, 70, 112], art: <polygon points="98,150 40,100 52,150 40,200" fill="#e5822a" stroke="#b9621a" strokeWidth="2.5" strokeLinejoin="round" /> },
    { id: 'fin', label: 'fin', at: [204, 74], box: [150, 46, 104, 56], art: <path d="M160 96 Q196 46 248 96 Z" fill="#e5822a" stroke="#b9621a" strokeWidth="2.5" /> },
    { id: 'body', label: 'body', at: [130, 182], box: [88, 86, 224, 128], art: <ellipse cx="200" cy="150" rx="110" ry="62" fill="#f29b38" stroke="#b9621a" strokeWidth="3" /> },
    { id: 'scales', label: 'scales', at: [168, 150], box: [130, 120, 80, 60], art: <g fill="none" stroke="#c9741f" strokeWidth="2">{[0, 1, 2].flatMap((r) => [0, 1, 2, 3].map((c) => <path key={`${r}${c}`} d={`M${140 + c * 18 + (r % 2) * 9} ${130 + r * 18} q9 10 18 0`} />))}</g> },
    { id: 'gills', label: 'gills', at: [244, 150], box: [228, 108, 30, 84], art: <path d="M246 114 Q228 150 246 186" fill="none" stroke="#b9621a" strokeWidth="4" strokeLinecap="round" /> },
    { id: 'eye', label: 'eye', at: [266, 130], box: [250, 114, 32, 32], art: <g><circle cx="266" cy="130" r="12" fill="#fff" stroke="#b9621a" strokeWidth="2" /><circle cx="269" cy="131" r="6" fill="#222" /></g> },
    { id: 'mouth', label: 'mouth', at: [306, 160], box: [292, 148, 26, 24], art: <path d="M309 154 Q298 160 309 166" fill="none" stroke="#7a3b0d" strokeWidth="3.5" strokeLinecap="round" /> },
  ],
};

const bird: Scene = {
  id: 'bird',
  label: 'Bird',
  kind: 'parts',
  w: 400,
  h: 300,
  base: <><rect width="400" height="300" fill="#eaf6ff" /><rect x="50" y="214" width="300" height="11" rx="5" fill="#8a5a33" /><ellipse cx="80" cy="212" rx="18" ry="8" fill="#5e9c45" /></>,
  parts: [
    { id: 'tail', label: 'tail', at: [92, 152], box: [70, 124, 74, 58], art: <polygon points="140,150 74,128 86,178" fill="#2f7fb8" stroke="#1f5d8c" strokeWidth="2" strokeLinejoin="round" /> },
    { id: 'body', label: 'body', at: [168, 186], box: [126, 94, 150, 112], art: <><ellipse cx="200" cy="152" rx="70" ry="52" fill="#4aa3df" stroke="#1f5d8c" strokeWidth="2.5" /><circle cx="265" cy="110" r="34" fill="#4aa3df" stroke="#1f5d8c" strokeWidth="2.5" /></> },
    { id: 'feathers', label: 'feathers', at: [234, 170], box: [200, 142, 60, 52], art: <ellipse cx="230" cy="168" rx="28" ry="22" fill="#a8d8f5" /> },
    { id: 'wing', label: 'wing', at: [196, 146], box: [160, 116, 86, 64], art: <path d="M166 136 Q214 112 242 150 Q206 178 164 160 Z" fill="#2f7fb8" stroke="#1f5d8c" strokeWidth="2" /> },
    { id: 'eye', label: 'eye', at: [276, 104], box: [264, 92, 24, 24], art: <g><circle cx="276" cy="104" r="7" fill="#fff" /><circle cx="277" cy="104" r="4" fill="#222" /></g> },
    { id: 'beak', label: 'beak', at: [318, 112], box: [292, 100, 42, 26], art: <polygon points="296,104 332,112 296,121" fill="#f2b425" stroke="#c78a12" strokeWidth="2" strokeLinejoin="round" /> },
    { id: 'feet', label: 'feet', at: [204, 208], box: [180, 196, 48, 26], art: <g stroke="#e3a33b" strokeWidth="4" strokeLinecap="round"><line x1="190" y1="198" x2="188" y2="216" /><line x1="214" y1="198" x2="216" y2="216" /></g> },
  ],
};

const dog: Scene = {
  id: 'dog',
  label: 'Dog',
  kind: 'parts',
  w: 400,
  h: 300,
  base: <><rect width="400" height="300" fill="#f3f8e9" /><rect y="250" width="400" height="50" fill="#b9d98f" /></>,
  parts: [
    { id: 'tail', label: 'tail', at: [86, 112], box: [70, 96, 52, 70], art: <path d="M112 158 Q90 140 84 108" fill="none" stroke="#c98d50" strokeWidth="12" strokeLinecap="round" /> },
    { id: 'legs', label: 'legs', at: [168, 228], box: [124, 190, 146, 70], art: <g fill="#c98d50">{[128, 158, 214, 244].map((x) => <rect key={x} x={x} y="192" width="18" height="56" rx="8" />)}</g> },
    { id: 'paws', label: 'paws', at: [138, 252], box: [122, 240, 150, 22], art: <g fill="#e2b07c">{[137, 167, 223, 253].map((x) => <ellipse key={x} cx={x} cy="250" rx="13" ry="7" />)}</g> },
    { id: 'fur', label: 'fur', at: [176, 168], box: [100, 126, 180, 90], art: <ellipse cx="190" cy="170" rx="86" ry="42" fill="#c98d50" stroke="#8a5a2e" strokeWidth="2.5" /> },
    { id: 'collar', label: 'collar', at: [266, 160], box: [246, 144, 56, 30], art: <path d="M250 148 Q272 168 300 152 L302 162 Q272 180 248 158 Z" fill="#d9583b" stroke="#a63e27" strokeWidth="1.5" /> },
    { id: 'head', label: 'head', at: [300, 92], box: [244, 84, 120, 84], art: <><circle cx="285" cy="125" r="38" fill="#c98d50" stroke="#8a5a2e" strokeWidth="2.5" /><ellipse cx="320" cy="138" rx="24" ry="16" fill="#e2b07c" /></> },
    { id: 'ear', label: 'ear', at: [256, 128], box: [244, 88, 34, 64], art: <path d="M272 94 Q244 104 252 146 Q266 140 278 108 Z" fill="#8a5a2e" /> },
    { id: 'eye', label: 'eye', at: [292, 114], box: [282, 104, 22, 22], art: <circle cx="292" cy="114" r="6" fill="#222" /> },
    { id: 'nose', label: 'nose', at: [340, 134], box: [328, 124, 24, 22], art: <circle cx="340" cy="134" r="7.5" fill="#2b2b2b" /> },
  ],
};

const SLOT: Record<string, [number, number, number]> = {
  head: [200, 26, 62],
  eyes: [200, 52, 42],
  neck: [200, 88, 48],
  body: [200, 130, 96],
  hands: [134, 172, 40],
  holding: [276, 150, 72],
  legs: [200, 236, 92],
  feet: [200, 294, 56],
  back: [252, 118, 60],
};

function wear(id: string, label: string, emoji: string, slot: keyof typeof SLOT, worn: ReactNode, mini?: ReactNode): ScenePart {
  const [x, y, size] = SLOT[slot];
  return { id, label, wear: emoji, wearSize: size, worn, mini, at: [x, y], box: [x - 30, y - 30, 60, 60] };
}

const LINE = { strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, fill: 'none' };

const pair = (left: ReactNode, right: ReactNode) => <>{left}{right}</>;

const TSHIRT = <g>
  <g {...LINE} stroke="#43884f" strokeWidth="28"><path d="M170 100 L158 123" /><path d="M230 100 L242 123" /></g>
  <g {...LINE} stroke="#5fae6e" strokeWidth="24"><path d="M170 100 L158 123" /><path d="M230 100 L242 123" /></g>
  <path d="M176 89 Q163 91 162 104 L164 174 Q164 180 170 180 L230 180 Q236 180 236 174 L238 104 Q237 91 224 89 Q214 99 200 99 Q186 99 176 89 Z" fill="#5fae6e" stroke="#43884f" strokeWidth="2" strokeLinejoin="round" />
</g>;

const mitten = (x: number, flip: boolean) => (
  <g transform={flip ? `translate(${2 * x} 0) scale(-1 1)` : undefined}>
    <ellipse cx={x - 11} cy={170} rx="5" ry="8" fill="#8b6fb8" stroke="#6a5190" strokeWidth="2" transform={`rotate(-25 ${x - 11} 170)`} />
    <path d={`M${x - 10} 168 Q${x - 10} 158 ${x} 158 Q${x + 10} 158 ${x + 10} 168 L${x + 10} 182 Q${x + 10} 188 ${x + 4} 188 L${x - 4} 188 Q${x - 10} 188 ${x - 10} 182 Z`} fill="#8b6fb8" stroke="#6a5190" strokeWidth="2" />
    <rect x={x - 11} y="156" width="22" height="7" rx="3" fill="#a891cc" stroke="#6a5190" strokeWidth="2" />
  </g>
);

const WORN = {
  trousers: <g>
    <g {...LINE} stroke="#3f6fb0" strokeWidth="23"><path d="M186 204 L184 245 L182 280" /><path d="M214 204 L216 245 L218 280" /></g>
    <rect x="166" y="166" width="68" height="44" rx="8" fill="#3f6fb0" stroke="#2f5690" strokeWidth="2" />
    <path d="M200 176 L200 208" stroke="#2f5690" strokeWidth="2" />
  </g>,
  shorts: <g>
    <g {...LINE} stroke="#d08a3c" strokeWidth="24"><path d="M186 204 L185 226" /><path d="M214 204 L215 226" /></g>
    <rect x="166" y="166" width="68" height="44" rx="8" fill="#d08a3c" stroke="#a96a26" strokeWidth="2" />
  </g>,
  shoes: pair(
    <path d="M160 296 Q160 284 172 284 L186 284 Q196 286 196 296 Q196 304 186 304 L168 304 Q160 304 160 296 Z" fill="#6a5acd" stroke="#4b3fa0" strokeWidth="2" />,
    <path d="M204 296 Q204 286 214 284 L228 284 Q240 284 240 296 Q240 304 232 304 L214 304 Q204 304 204 296 Z" fill="#6a5acd" stroke="#4b3fa0" strokeWidth="2" />,
  ),
  boots: pair(
    <path d="M172 262 L194 262 L196 296 Q196 304 186 304 L166 304 Q158 304 158 296 Q158 286 172 284 Z" fill="#7a5230" stroke="#56391f" strokeWidth="2" />,
    <path d="M206 262 L228 262 L228 284 Q242 286 242 296 Q242 304 234 304 L214 304 Q204 304 204 296 Z" fill="#7a5230" stroke="#56391f" strokeWidth="2" />,
  ),
  wellies: pair(
    <path d="M171 248 L195 248 L196 296 Q196 304 186 304 L166 304 Q158 304 158 296 Q158 286 171 284 Z" fill="#3f8f5a" stroke="#2c6a41" strokeWidth="2" />,
    <path d="M205 248 L229 248 L229 284 Q242 286 242 296 Q242 304 234 304 L214 304 Q204 304 204 296 Z" fill="#3f8f5a" stroke="#2c6a41" strokeWidth="2" />,
  ),
  sandals: <g>
    <ellipse cx="178" cy="302" rx="18" ry="5" fill="#b07a45" />
    <ellipse cx="222" cy="302" rx="18" ry="5" fill="#b07a45" />
    <g {...LINE} stroke="#8a5a2e" strokeWidth="4"><path d="M168 294 Q178 288 188 294" /><path d="M212 294 Q222 288 232 294" /></g>
  </g>,
  tshirt: <g>
    <path d="M172 86 L228 86 L226 92 Q214 101 200 101 Q186 101 174 92 Z" fill={SKIN} />
    {TSHIRT}
  </g>,
  jumper: <g>
    <g {...LINE} stroke="#b4545e" strokeWidth="22"><path d="M168 100 L150 133 L139 158" /><path d="M232 100 L250 133 L261 158" /></g>
    <rect x="162" y="88" width="76" height="94" rx="14" fill="#b4545e" stroke="#8c3d46" strokeWidth="2" />
    <path d="M164 172 L236 172" stroke="#8c3d46" strokeWidth="3" />
    <path d="M188 89 Q200 98 212 89" fill="none" stroke="#8c3d46" strokeWidth="3" />
  </g>,
  coat: <g>
    <g {...LINE} stroke="#5b6f8f" strokeWidth="24"><path d="M168 100 L150 133 L139 158" /><path d="M232 100 L250 133 L261 158" /></g>
    <path d="M160 96 Q160 86 174 86 L226 86 Q240 86 240 96 L244 218 L156 218 Z" fill="#5b6f8f" stroke="#43546d" strokeWidth="2" strokeLinejoin="round" />
    <path d="M200 92 L200 216" stroke="#43546d" strokeWidth="2" />
    <g fill="#e8e1d2"><circle cx="207" cy="120" r="3" /><circle cx="207" cy="150" r="3" /><circle cx="207" cy="180" r="3" /></g>
  </g>,
  scarf: <g>
    <rect x="180" y="78" width="40" height="16" rx="7" fill="#c8513f" stroke="#9d3a2c" strokeWidth="2" />
    <path d="M206 90 L214 132 L200 134 L196 92 Z" fill="#c8513f" stroke="#9d3a2c" strokeWidth="2" strokeLinejoin="round" />
  </g>,
  gloves: pair(mitten(134, true), mitten(266, false)),
  bag: <g>
    <path d="M176 92 L246 150" stroke="#b07f24" strokeWidth="7" strokeLinecap="round" />
    <rect x="234" y="140" width="48" height="40" rx="8" fill="#d9a441" stroke="#b07f24" strokeWidth="2" />
    <path d="M234 154 L282 154" stroke="#b07f24" strokeWidth="2" />
    <rect x="253" y="150" width="10" height="8" rx="2" fill="#b07f24" />
  </g>,
  hat: <g>
    <path d="M168 42 Q168 14 200 14 Q232 14 232 42 Z" fill="#3f6fb0" stroke="#2f5690" strokeWidth="2" />
    <path d="M164 37 Q200 47 236 37 L236 43 Q200 52 164 43 Z" fill="#2f5690" stroke="#264878" strokeWidth="1.5" strokeLinejoin="round" />
  </g>,
  sunhat: <g>
    <ellipse cx="200" cy="34" rx="56" ry="11" fill="#e3c98a" stroke="#b89a55" strokeWidth="2" />
    <path d="M174 34 Q176 8 200 8 Q224 8 226 34 Z" fill="#e3c98a" stroke="#b89a55" strokeWidth="2" />
    <path d="M175 30 L225 30" stroke="#c8513f" strokeWidth="4" />
  </g>,
  sunglasses: <g>
    <rect x="179" y="45" width="19" height="13" rx="5" fill="#2b2b2b" />
    <rect x="202" y="45" width="19" height="13" rx="5" fill="#2b2b2b" />
    <path d="M198 50 L202 50 M179 50 L171 47 M221 50 L229 47" stroke="#2b2b2b" strokeWidth="2.5" />
  </g>,
  umbrella: <g>
    <path d="M266 172 L266 104" stroke="#555" strokeWidth="4" strokeLinecap="round" />
    <path d="M266 178 Q266 186 258 186" fill="none" stroke="#555" strokeWidth="4" strokeLinecap="round" />
    <path d="M222 106 Q266 54 310 106 Q299 98 288 106 Q277 98 266 106 Q255 98 244 106 Q233 98 222 106 Z" fill="#4f8fc0" stroke="#3a6f99" strokeWidth="2" strokeLinejoin="round" />
  </g>,
};

const person: Scene = {
  id: 'person',
  label: 'Person to dress',
  kind: 'dress',
  w: 400,
  h: 320,
  base: <><rect width="400" height="320" fill="#f6f1fb" /><rect y="300" width="400" height="20" fill="#e2d8ef" />{figure(false)}</>,
  parts: [
    wear('trousers', 'trousers', '👖', 'legs', WORN.trousers),
    wear('shorts', 'shorts', '🩳', 'legs', WORN.shorts),
    wear('shoes', 'shoes', '👟', 'feet', WORN.shoes),
    wear('boots', 'boots', '🥾', 'feet', WORN.boots),
    wear('wellies', 'wellies', '👢', 'feet', WORN.wellies),
    wear('sandals', 'sandals', '🩴', 'feet', WORN.sandals),
    wear('tshirt', 't-shirt', '👕', 'body', WORN.tshirt, TSHIRT),
    wear('jumper', 'jumper', '🥼', 'body', WORN.jumper),
    wear('coat', 'coat', '🧥', 'body', WORN.coat),
    wear('bag', 'school bag', '🎒', 'back', WORN.bag),
    wear('scarf', 'scarf', '🧣', 'neck', WORN.scarf),
    wear('gloves', 'gloves', '🧤', 'hands', WORN.gloves, pair(mitten(184, true), mitten(216, false))),
    wear('hat', 'hat', '🧢', 'head', WORN.hat),
    wear('sunhat', 'sun hat', '👒', 'head', WORN.sunhat),
    wear('sunglasses', 'sunglasses', '🕶️', 'eyes', WORN.sunglasses),
    wear('umbrella', 'umbrella', '☂️', 'holding', WORN.umbrella),
  ],
};

const room: Scene = {
  id: 'room',
  label: 'Room',
  kind: 'room',
  w: 400,
  h: 300,
  base: (
    <>
      <rect width="400" height="200" fill="#f6efe3" />
      <rect y="200" width="400" height="100" fill="#e2cda8" />
      <rect x="250" y="30" width="70" height="56" rx="4" fill="#cfe8f7" stroke="#b9925c" strokeWidth="4" />
      <rect x="40" y="148" width="142" height="14" rx="4" fill="#a8713f" />
      <rect x="52" y="160" width="10" height="66" fill="#8a5a33" />
      <rect x="160" y="160" width="10" height="66" fill="#8a5a33" />
    </>
  ),
  mid: <polygon points="214,186 286,186 278,168 222,168" fill="#b98546" />,
  front: (
    <>
      <rect x="210" y="184" width="80" height="48" rx="3" fill="#d9a15f" stroke="#b98546" strokeWidth="2" />
      <rect x="296" y="236" width="8" height="22" fill="#6b4a2b" />
      <rect x="386" y="236" width="8" height="22" fill="#6b4a2b" />
      <rect x="292" y="210" width="106" height="30" rx="5" fill="#8a5a33" />
      <rect x="296" y="194" width="100" height="18" rx="6" fill="#fff" stroke="#d9d0c0" />
      <ellipse cx="374" cy="190" rx="18" ry="9" fill="#cfe1f5" />
    </>
  ),
  parts: [
    { id: 'on-table', label: 'on the table', at: [110, 128], box: [80, 100, 60, 50], hit: [44, 96, 136, 52] },
    { id: 'under-table', label: 'under the table', at: [112, 204], box: [80, 176, 60, 50], hit: [66, 166, 92, 62] },
    { id: 'next-table', label: 'next to the table', at: [24, 214], box: [0, 186, 40, 50], hit: [0, 170, 38, 70] },
    { id: 'in-box', label: 'in the box', at: [250, 186], box: [220, 160, 60, 50], hit: [212, 164, 76, 40], inside: true },
    { id: 'behind-box', label: 'behind the box', at: [250, 162], box: [220, 130, 60, 50], hit: [214, 118, 72, 44], behind: true },
    { id: 'next-box', label: 'next to the box', at: [190, 226], box: [180, 210, 50, 50], hit: [186, 206, 22, 50] },
    { id: 'on-bed', label: 'on the bed', at: [330, 178], box: [300, 150, 60, 50], hit: [296, 150, 100, 44] },
    { id: 'under-bed', label: 'under the bed', at: [345, 250], box: [316, 226, 60, 50], hit: [300, 242, 90, 50], behind: true },
  ],
};

export const SCENES: Record<string, Scene> = { house, plane, face, body, car, bicycle, flower, fish, bird, dog, person, room, ...Object.fromEntries(PLACES.map((p) => [p.id, p])) };

export const PART_SCENES = ['house', 'plane', 'face', 'body', 'car', 'bicycle', 'flower', 'fish', 'bird', 'dog'];
export const DRESS_SCENES = ['person'];
export const ROOM_SCENES = ['room'];
export const PLACE_SCENES = PLACES.map((p) => p.id);

export function sceneOf(id: unknown): Scene | null {
  return typeof id === 'string' && SCENES[id] ? SCENES[id] : null;
}
